import {
  customerMarketShopfrontJobsApi,
  marketApi
} from '@envato/foundation-types'
import { ThunkAction } from 'redux-thunk'
import { get } from 'lodash-es'

import { setSeoMeta } from '../actions/common'
import { reportPageType } from '../analytics'
import axiosVerbose from '../utils/axios/axiosVerbose'
import { logError } from '../utils/errors'
import { getAllItemsIds, limitItemsForNewDesign } from '../utils/item'

import { PageFetchAction } from '../../typings/pageComponent'
import { ISeoMetadata } from '../../typings/seoMetadata'
import { IItem } from '../../typings/searchResults'
import { reportProductsImpressions } from '../analytics/resultAnalytics'
import { hasFeature } from '../utils/features'
import { IRootState } from '../reducers'
import { getBookmarksForItems } from './user/bookmark'

export enum TypeKeys {
  GET_HOME_PAGE_API_SUCCESS = 'GET_HOME_PAGE_API_SUCCESS',
  GET_HOME_PAGE_API_FAIL = 'GET_HOME_PAGE_API_FAIL'
}

export interface IHomePageApiData {
  readonly metadata?: ISeoMetadata
  readonly newest_items?: customerMarketShopfrontJobsApi.INewestItemsResult
  readonly featured_items?: customerMarketShopfrontJobsApi.IBaseResult
  readonly free_items?: customerMarketShopfrontJobsApi.IBaseResult
  readonly featured_author?: customerMarketShopfrontJobsApi.IFeaturedAuthorWithItemsResult
  readonly categories?: marketApi.CategoriesResult
}

export interface IGetHomePageApiSuccessAction {
  type: TypeKeys.GET_HOME_PAGE_API_SUCCESS
  payload: Readonly<IHomePageApiData>
}

export interface IGetHomePageApiFailAction {
  type: TypeKeys.GET_HOME_PAGE_API_FAIL
}

export type ActionTypes =
  | IGetHomePageApiSuccessAction
  | IGetHomePageApiFailAction

export const onBeforeHomePageView = (): ThunkAction<void, IRootState, void> => (
  dispatch,
  getState
) => {
  const {
    featuredItems,
    freeItems,
    featuredAuthor,
    newestItems
  } = getState().homePage
  const currentSite = getState().common.currentSite

  reportPageType('home')

  if (featuredItems) {
    reportProductsImpressions({
      items: featuredItems.matches.slice(0, 10),
      currentSite,
      itemRankOffset: 1,
      page: 'Homepage',
      section: 'Featured items'
    })
  }

  if (newestItems) {
    reportProductsImpressions({
      items: get(newestItems, 'all.matches', []) as IItem[],
      currentSite,
      itemRankOffset: 1,
      page: 'Homepage',
      section: 'New items'
    })
  }

  if (freeItems) {
    reportProductsImpressions({
      items: freeItems.matches.slice(0, 7),
      currentSite,
      itemRankOffset: 1,
      page: 'Homepage',
      section: 'Free files of the month'
    })
  }

  if (featuredAuthor) {
    reportProductsImpressions({
      items: featuredAuthor.matches.slice(0, 3),
      currentSite,
      itemRankOffset: 1,
      page: 'Homepage',
      section: 'Featured authors'
    })
  }
}

export const fetchHomePageDataBySite: PageFetchAction<
  ThunkAction<Promise<ActionTypes>, IRootState, void>
> = ({ apiAppUrlPrefix, location }) => {
  return (dispatch, getState) => {
    const { currentSite } = getState().common
    const { experiments } = getState()

    const url = `${apiAppUrlPrefix}/shopfront-api/homepage/${currentSite}`

    return axiosVerbose
      .get(url)
      .then(response => {
        const isRedesign = hasFeature(
          'homepageNewDesign',
          currentSite,
          location,
          experiments
        )

        const data = isRedesign
          ? limitItemsForNewDesign(response.data)
          : response.data

        const action: IGetHomePageApiSuccessAction = {
          payload: data,
          type: TypeKeys.GET_HOME_PAGE_API_SUCCESS
        }
        dispatch(setSeoMeta(data.metadata))

        if (isRedesign) {
          const itemIdsForBookmarks = getAllItemsIds(data)
          dispatch(getBookmarksForItems(itemIdsForBookmarks))
        }

        return dispatch(action)
      })
      .catch(err => Promise.reject(logError(err)))
      .catch(() => {
        const action: IGetHomePageApiFailAction = {
          type: TypeKeys.GET_HOME_PAGE_API_FAIL
        }
        return dispatch(action)
      })
  }
}
